/**
 * Other Statistics — shared session between the grid band and the detail
 * pane. The band owns the data (its react-query fetch) and the writes; the
 * pane, mounted by WorkspacePage in the standard right-hand slot, reads the
 * selected row from here and calls the band's handlers. Keeps stat accounts
 * out of the money-account selection (ForecastV3Context.selectedAccount),
 * whose shape and driver catalog don't fit a count.
 */

import { useSyncExternalStore } from 'react';
import type { StatDriverConfig, StatDriverDef, StatValueItem } from '../../api';
import type { ForecastAccount } from '../../types';
import type { StatRow } from '../../utils/otherStatsGrid';

export interface OtherStatsPublished {
  rows: StatRow[];
  driverDefs: StatDriverDef[];
  revenueAccounts: ForecastAccount[];
  canEdit: boolean;
  isBudget: boolean;
  year: number | undefined;
  /** Set / clear a row's driver. */
  saveDriver: (row: StatRow, config: StatDriverConfig) => Promise<void>;
  /** Write keyed month values (value null clears the override). */
  saveKeyed: (items: StatValueItem[]) => Promise<void>;
}

interface Session {
  selectedId: number | null;
  published: OtherStatsPublished | null;
}

let session: Session = { selectedId: null, published: null };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export const selectStatAccount = (chartOfAccountId: number | null) => {
  if (session.selectedId === chartOfAccountId) return;
  session = { ...session, selectedId: chartOfAccountId };
  emit();
};

export const publishOtherStats = (published: OtherStatsPublished | null) => {
  session = { ...session, published };
  emit();
};

const getSession = () => session;

/** The selected stat account id (null when the pane is closed). */
export function useSelectedStatAccountId(): number | null {
  return useSyncExternalStore(subscribe, () => getSession().selectedId);
}

/** Everything the pane needs: the selected row plus the band's handlers. */
export function useOtherStatsSession(): { selectedId: number | null; published: OtherStatsPublished | null; row: StatRow | null } {
  const s = useSyncExternalStore(subscribe, getSession);
  const row = s.selectedId !== null ? s.published?.rows.find((r) => r.account.chartOfAccountId === s.selectedId) ?? null : null;
  return { selectedId: s.selectedId, published: s.published, row };
}
